import { useState } from "react";
import { Instagram, Facebook, Music2, MessageCircle, Globe, ExternalLink, ArrowRight, QrCode } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { pathFor } from "../lib/routing";
import logo from "../assets/logo.png";
import qrInternational from "../assets/qr-international.jpg";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export default function Footer() {
  const { t, lang } = useI18n();
  const [qrOpen, setQrOpen] = useState(false);
  return (
    <footer className="border-t border-white/5 bg-charred" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={logo} alt="Asado Xperience" className="h-10 w-10 object-contain shrink-0" />
            <span className="font-display text-base tracking-[0.28em] uppercase">
              Asado <span className="text-ember">Xperience</span>
            </span>
          </div>
          <p className="text-[#A3A3A3] max-w-md leading-relaxed text-sm">
            {t.footer.tagline}
          </p>
          <div className="mt-6">
            <p className="overline mb-3">{t.nav.location}</p>
            <p className="text-[#F5F3F0] text-sm leading-relaxed">
              Palermo Soho<br />C1414 · Buenos Aires<br />Argentina
            </p>
          </div>
          <div className="mt-6">
            <p className="overline mb-3">{t.footer.explore}</p>
            <Link to={pathFor("palermo", lang)}
                  className="inline-flex items-center gap-2 text-[#F5F3F0] hover:text-ember transition-colors text-sm"
                  data-testid="footer-palermo-link">
              {t.footer.palermo_link} <ArrowRight size={13} />
            </Link>
            <Link to={pathFor("curso", lang)}
                  className="mt-3 flex items-center gap-2 text-[#F5F3F0] hover:text-ember transition-colors text-sm"
                  data-testid="footer-curso-link">
              {t.footer.curso_link} <ArrowRight size={13} />
            </Link>
          </div>
        </div>

        <div>
          <p className="overline mb-4">Redes</p>
          <ul className="space-y-3 text-sm">
            <li>
              <a href="https://www.instagram.com/asadoxperience" target="_blank" rel="noreferrer"
                 className="flex items-center gap-2 text-[#F5F3F0] hover:text-ember transition-colors" data-testid="footer-ig-link">
                <Instagram size={14} /> @asadoxperience <ExternalLink size={12} className="opacity-60" />
              </a>
            </li>
            <li>
              <a href="https://www.tiktok.com/@asadoxperiences" target="_blank" rel="noreferrer"
                 className="flex items-center gap-2 text-[#F5F3F0] hover:text-ember transition-colors" data-testid="footer-tiktok-link">
                <Music2 size={14} /> @asadoxperiences <ExternalLink size={12} className="opacity-60" />
              </a>
            </li>
            <li>
              <a href="https://www.facebook.com/asadoxperience" target="_blank" rel="noreferrer"
                 className="flex items-center gap-2 text-[#F5F3F0] hover:text-ember transition-colors" data-testid="footer-fb-link">
                <Facebook size={14} /> asadoxperience <ExternalLink size={12} className="opacity-60" />
              </a>
            </li>
            <li>
              <a href="https://wa.me/5491151023381" target="_blank" rel="noreferrer"
                 className="flex items-center gap-2 text-[#F5F3F0] hover:text-ember transition-colors" data-testid="footer-wa-link">
                <MessageCircle size={14} /> +54 11 5102-3381 <ExternalLink size={12} className="opacity-60" />
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="overline mb-4">Reservá también en</p>
          <ul className="space-y-3 text-sm">
            <li>
              <a href="https://airbnb.com/x/asadoargentino" target="_blank" rel="noreferrer"
                 className="flex items-center gap-2 text-[#F5F3F0] hover:text-ember transition-colors" data-testid="footer-airbnb-link">
                <Globe size={14} /> Airbnb <ExternalLink size={12} className="opacity-60" />
              </a>
            </li>
          </ul>
          <a href="https://airbnb.com/x/asadoargentino" target="_blank" rel="noreferrer"
             className="mt-5 inline-flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
             aria-label="Reservá en Airbnb" data-testid="footer-airbnb-logo">
            <svg viewBox="0 0 24 24" className="h-7 w-7" fill="#FF385C" aria-hidden="true">
              <path d="M12.001 18.275c-1.353-1.697-2.148-3.184-2.413-4.457-.263-1.027-.16-1.848.291-2.465.477-.71 1.188-1.056 2.121-1.056s1.643.345 2.12 1.063c.446.61.558 1.432.286 2.465-.291 1.298-1.085 2.785-2.412 4.458zm9.601 1.14c-.185 1.246-1.034 2.28-2.2 2.783-2.253.98-4.483-.583-6.392-2.704 3.157-3.951 3.74-7.028 2.385-9.018-.795-1.14-1.933-1.695-3.394-1.695-2.944 0-4.563 2.49-3.927 5.382.37 1.565 1.352 3.343 2.917 5.332-.98 1.085-1.91 1.856-2.732 2.333-.636.344-1.245.558-1.828.609-2.679.399-4.778-2.2-3.825-4.88.132-.345.395-.98.845-1.961l.025-.053c1.464-3.178 3.242-6.79 5.285-10.795l.053-.132.58-1.116c.45-.822.635-1.19 1.351-1.643.346-.21.77-.315 1.246-.315.954 0 1.698.558 2.016 1.007.158.239.345.557.582.953l.558 1.089.08.159c2.041 4.004 3.821 7.608 5.279 10.794l.026.025.533 1.22.318.764c.243.613.294 1.222.213 1.858zm1.22-2.39c-.186-.583-.505-1.271-.9-2.094v-.03c-1.889-4.006-3.642-7.608-5.307-10.844l-.111-.163C15.317 1.461 14.468 0 12.001 0c-2.44 0-3.476 1.695-4.535 3.898l-.081.16c-1.669 3.236-3.421 6.843-5.303 10.847v.053l-.559 1.22c-.21.504-.317.768-.345.847C-.172 20.74 2.611 24 5.98 24c.027 0 .132 0 .265-.027h.372c1.75-.213 3.554-1.325 5.384-3.317 1.829 1.989 3.635 3.104 5.382 3.317h.372c.133.027.239.027.265.027 3.37.003 6.152-3.261 4.802-6.975z" />
            </svg>
            <span className="text-xl font-semibold tracking-tight" style={{ color: "#FF385C" }}>airbnb</span>
          </a>
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            className="mt-4 flex items-center gap-2 text-[#A3A3A3] hover:text-ember transition-colors text-sm"
            data-testid="footer-international-qr-trigger"
          >
            <QrCode size={14} /> {t.footer.international}
          </button>
        </div>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-charred border-white/10 text-[#F5F3F0] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">{t.footer.international_modal_title}</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">{t.footer.international_modal_desc}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <img
              src={qrInternational}
              alt={t.footer.international_modal_title}
              className="w-56 h-56 object-contain rounded-lg bg-white p-3"
            />
          </div>
        </DialogContent>
      </Dialog>
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between text-xs text-[#A3A3A3]">
          <p>© {new Date().getFullYear()} Asado Xperience — Diego Blaksley. {t.footer.rights}</p>
          <p className="mt-2 md:mt-0 tracking-[0.2em] uppercase">Palermo · Buenos Aires</p>
        </div>
      </div>
    </footer>
  );
}
